import { AlertTriangle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/auth/useAuth'
import { api } from '@/lib/api'
import { tokenManager } from '@/lib/tokenManager'

/**
 * Mounted by AppShell. Renders only when the current JWT carries
 * is_impersonating=true. Hitting "Stop impersonating" calls
 * /api/auth/impersonate/stop, restores any saved super-admin token,
 * and navigates back to the platform admin (or /login on cleanup).
 */
export function ImpersonationBanner() {
  const { user, isImpersonating } = useAuth()
  const navigate = useNavigate()

  if (!user || !isImpersonating) return null

  const stop = async () => {
    try {
      await api.post('/api/auth/impersonate/stop')
      const saved =
        typeof sessionStorage !== 'undefined'
          ? sessionStorage.getItem('bk.super_admin_token')
          : null
      if (saved) {
        tokenManager.set(saved)
        try {
          sessionStorage.removeItem('bk.super_admin_token')
        } catch {
          /* ignore */
        }
        navigate('/platform/tenants', { replace: true })
      } else {
        tokenManager.clear()
        navigate('/login', { replace: true })
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to stop impersonation', e)
    }
  }

  return (
    <div className="flex items-center gap-3 border-b border-warning/40 bg-warning/15 px-4 py-2 text-sm text-warning-foreground">
      <AlertTriangle className="size-4" aria-hidden />
      <span className="flex-1">
        You are impersonating <strong>{user.email || 'tenant'}</strong>. Original admin:{' '}
        <span className="font-mono text-xs">{user.originalSub ?? '-'}</span>. All actions are
        audited.
      </span>
      <Button size="sm" variant="outline" onClick={() => void stop()}>
        Stop impersonating
      </Button>
    </div>
  )
}
